import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  Group,
  NumberInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowLeft, IconSearch } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useCreateRoom, useUpdateRoom } from 'src/Hooks/Mutations/useCartographyMutations';
import { trackRoomTypeSearch } from 'src/Services/Mixpanel/events';
import { RoomFeature, RoomType } from 'src/types/cartography.types';

import {
  COMMON_ROOM_TYPES,
  DEFAULT_FEATURES_BY_TYPE,
  isSleepingRoomType,
  isWashroomType,
  ROOM_CATEGORIES,
  ROOM_FEATURES,
  ROOM_ICONS,
  RoomCategoryKey,
  searchRoomTypes,
} from '../../referential';

import styles from './RoomDrawer.module.scss';
import { Props } from './RoomDrawer.types';

const NO_FLOOR = '__no_floor__';
const NO_SERVED_ROOM = '__none__';

/** Étape 1 : quelle pièce ? Étape 2 : lequel de ces types ? Étape 3 : ses détails. */
type Step = 'category' | 'type' | 'details';

const RoomDrawer = ({
  opened,
  buildingId,
  floors,
  defaultFloorId,
  room,
  bedrooms,
  onClose,
  onRoomCreated,
}: Props) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>('category');
  const [category, setCategory] = useState<RoomCategoryKey | null>(null);
  const [search, setSearch] = useState('');

  const [name, setName] = useState('');
  const [type, setType] = useState<RoomType>(RoomType.BEDROOM);
  const [features, setFeatures] = useState<RoomFeature[]>([]);
  const [servedRoomId, setServedRoomId] = useState<string>(NO_SERVED_ROOM);
  const [floorId, setFloorId] = useState<string>(NO_FLOOR);
  const [areaSqm, setAreaSqm] = useState<number | ''>('');

  const isEdit = !!room;

  const labelOf = useMemo(
    () => (roomType: RoomType) => t(`admin.structure.roomTypes.${roomType}`, roomType),
    [t]
  );

  useEffect(() => {
    if (!opened) return;

    setName(room?.name ?? '');
    setType(room?.type ?? RoomType.BEDROOM);
    setFeatures(room?.features ?? []);
    setServedRoomId(room?.servedRoomId ?? NO_SERVED_ROOM);
    setFloorId(room?.floorId ?? defaultFloorId ?? NO_FLOOR);
    setAreaSqm(room?.areaSqm ?? '');
    setSearch('');
    setCategory(null);
    // En édition le type est déjà connu : on ouvre directement sur les détails.
    setStep(room ? 'details' : 'category');
  }, [opened, room, defaultFloorId]);

  const { mutate: createRoom, isLoading: isCreating } = useCreateRoom({
    onSuccess: created => {
      onClose();
      onRoomCreated?.(created);
    },
  });
  const { mutate: updateRoom, isLoading: isUpdating } = useUpdateRoom({ onSuccess: onClose });

  const searchResults = useMemo(
    () => (search.trim().length > 0 ? searchRoomTypes(search, labelOf) : []),
    [search, labelOf]
  );

  /**
   * Choisir un type pré-remplit les équipements attendus et, si le nom est encore vide,
   * le nom de la pièce — l'alias saisi prime pour rester au plus près du mot du foyer.
   * En édition, le nom saisi est préservé ; les équipements et la chambre desservie
   * sont ajustés pour rester cohérents avec le nouveau type.
   */
  const selectType = (nextType: RoomType, matchedAlias?: string) => {
    const previousType = type;
    setType(nextType);
    if (!isEdit) {
      setFeatures(DEFAULT_FEATURES_BY_TYPE[nextType] ?? []);
      if (name.trim().length === 0) {
        setName(matchedAlias ? capitalize(matchedAlias) : labelOf(nextType));
      }
    } else if (nextType !== previousType) {
      if (!isWashroomType(nextType)) {
        setFeatures([]);
      } else if (!isWashroomType(previousType) && features.length === 0) {
        setFeatures(DEFAULT_FEATURES_BY_TYPE[nextType] ?? []);
      }
    }
    if (!isWashroomType(nextType)) setServedRoomId(NO_SERVED_ROOM);
    setStep('details');
  };

  const handleSearchSubmit = (matchCount: number) => {
    if (search.trim().length === 0) return;
    trackRoomTypeSearch({ query_length: search.trim().length, results_count: matchCount });
  };

  const toggleFeature = (feature: RoomFeature) => {
    setFeatures(current =>
      current.includes(feature) ? current.filter(f => f !== feature) : [...current, feature]
    );
  };

  const handleSubmit = () => {
    if (!name.trim()) return;
    const payload = {
      buildingId,
      floorId: floorId === NO_FLOOR ? undefined : floorId,
      name: name.trim(),
      type,
      features,
      servedRoomId: servedRoomId === NO_SERVED_ROOM ? null : servedRoomId,
      areaSqm: areaSqm === '' ? undefined : Number(areaSqm),
    };
    if (isEdit && room) {
      updateRoom({ roomId: room.id, payload });
    } else {
      createRoom(payload);
    }
  };

  const floorOptions = [
    { value: NO_FLOOR, label: t('admin.structure.rooms.modal.floorNone', 'Sans niveau') },
    ...floors.map(f => ({ value: f.id, label: f.name })),
  ];

  const servedRoomOptions = [
    { value: NO_SERVED_ROOM, label: t('admin.structure.rooms.modal.servedRoomNone', 'Aucune') },
    ...bedrooms
      .filter(candidate => candidate.id !== room?.id && isSleepingRoomType(candidate.type))
      .map(candidate => ({ value: candidate.id, label: candidate.name })),
  ];

  const currentCategory = ROOM_CATEGORIES.find(c => c.key === category);

  const renderTypeTile = (roomType: RoomType, matchedAlias?: string) => {
    const Icon = ROOM_ICONS[roomType];
    return (
      <UnstyledButton
        key={`${roomType}-${matchedAlias ?? ''}`}
        className={styles.tile}
        onClick={() => selectType(roomType, matchedAlias)}
      >
        <Icon size={22} className={styles.tileIcon} />
        <span className={styles.tileLabel}>{labelOf(roomType)}</span>
        {matchedAlias && <span className={styles.tileAlias}>{matchedAlias}</span>}
      </UnstyledButton>
    );
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="lg"
      padding={20}
      title={
        isEdit
          ? t('admin.structure.rooms.modal.editTitle', 'Modifier la pièce')
          : t('admin.structure.rooms.modal.createTitle', 'Nouvelle pièce')
      }
    >
      {step === 'category' && (
        <Stack gap="md">
          <TextInput
            leftSection={<IconSearch size={16} />}
            label={t('admin.structure.rooms.modal.searchLabel', 'Rechercher un type de pièce')}
            placeholder={t(
              'admin.structure.rooms.modal.searchPlaceholder',
              'Ex. souillarde, grenier, salle d’eau…'
            )}
            value={search}
            onChange={e => setSearch(e.currentTarget.value)}
            onBlur={() => handleSearchSubmit(searchResults.length)}
            data-autofocus
          />

          {search.trim().length > 0 ? (
            <div>
              {searchResults.length === 0 ? (
                <Text size="sm" c="dimmed">
                  {t('admin.structure.rooms.modal.typeNoMatch', 'Aucun type trouvé')}
                </Text>
              ) : (
                <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                  {searchResults.map(match => renderTypeTile(match.type, match.matchedAlias))}
                </SimpleGrid>
              )}
            </div>
          ) : (
            <>
              <div>
                <Text size="sm" fw={600} mb={8}>
                  {t('admin.structure.rooms.modal.commonTypes', 'Les plus courantes')}
                </Text>
                <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="xs">
                  {COMMON_ROOM_TYPES.map(roomType => renderTypeTile(roomType))}
                </SimpleGrid>
              </div>

              <div>
                <Text size="sm" fw={600} mb={8}>
                  {t('admin.structure.rooms.modal.allCategories', 'Toutes les familles')}
                </Text>
                <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
                  {ROOM_CATEGORIES.map(({ key, icon: Icon }) => (
                    <UnstyledButton
                      key={key}
                      className={styles.tile}
                      onClick={() => {
                        setCategory(key);
                        setStep('type');
                      }}
                    >
                      <Icon size={22} className={styles.tileIcon} />
                      <span className={styles.tileLabel}>
                        {t(`admin.structure.roomCategories.${key}`, key)}
                      </span>
                    </UnstyledButton>
                  ))}
                </SimpleGrid>
              </div>
            </>
          )}
        </Stack>
      )}

      {step === 'type' && currentCategory && (
        <Stack gap="md">
          <Button
            variant="outline"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => setStep('category')}
            className={styles.backButton}
          >
            {t('admin.structure.rooms.modal.back', 'Retour')}
          </Button>
          <Text size="sm" fw={600}>
            {t(`admin.structure.roomCategories.${currentCategory.key}`, currentCategory.key)}
          </Text>
          <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="xs">
            {currentCategory.types.map(roomType => renderTypeTile(roomType))}
          </SimpleGrid>
        </Stack>
      )}

      {step === 'details' && (
        <Stack gap="md">
          <Button
            variant="outline"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => setStep('category')}
            className={styles.backButton}
          >
            {t('admin.structure.rooms.modal.changeType', 'Changer de type')}
          </Button>

          <Group gap={8}>
            <Text size="sm" c="dimmed">
              {t('admin.structure.rooms.modal.type', 'Type de pièce')}
            </Text>
            <Badge variant="light">{labelOf(type)}</Badge>
          </Group>

          {isEdit && (
            <Text size="xs" c="dimmed">
              {t(
                'admin.structure.rooms.modal.typeHelp',
                'Le type sert à compter les pièces du bien.'
              )}
            </Text>
          )}

          <TextInput
            label={t('admin.structure.rooms.modal.name', 'Nom')}
            placeholder={t('admin.structure.rooms.modal.namePlaceholder', 'Chambre parentale')}
            value={name}
            onChange={e => setName(e.currentTarget.value)}
            required
            data-autofocus
          />

          {isWashroomType(type) && (
            <>
              <div>
                <Text size="sm" fw={600} mb={8}>
                  {t('admin.structure.rooms.modal.featuresLabel', 'Équipements')}
                </Text>
                <SimpleGrid cols={{ base: 2, sm: 3 }} spacing={6}>
                  {ROOM_FEATURES.map(feature => (
                    <Checkbox
                      key={feature}
                      checked={features.includes(feature)}
                      onChange={() => toggleFeature(feature)}
                      label={t(`admin.structure.roomFeatures.${feature}`, feature)}
                    />
                  ))}
                </SimpleGrid>
              </div>

              <Select
                label={t(
                  'admin.structure.rooms.modal.servedRoom',
                  'Attenante à une chambre (en suite)'
                )}
                data={servedRoomOptions}
                value={servedRoomId}
                onChange={value => setServedRoomId(value ?? NO_SERVED_ROOM)}
              />
            </>
          )}

          <Select
            label={t('admin.structure.rooms.modal.floor', 'Niveau')}
            data={floorOptions}
            value={floorId}
            onChange={value => setFloorId(value ?? NO_FLOOR)}
          />

          <NumberInput
            label={t('admin.structure.rooms.modal.areaSqm', 'Surface (m²)')}
            value={areaSqm}
            onChange={value => setAreaSqm(value === '' ? '' : Number(value))}
            min={0}
            decimalScale={1}
          />

          <Group justify="space-between">
            <Button variant="outline" onClick={onClose}>
              {t('admin.structure.rooms.modal.cancel', 'Annuler')}
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              loading={isCreating || isUpdating}
              disabled={!name.trim()}
            >
              {t('admin.structure.rooms.modal.save', 'Enregistrer')}
            </Button>
          </Group>
        </Stack>
      )}
    </Drawer>
  );
};

const capitalize = (value: string): string => value.charAt(0).toUpperCase() + value.slice(1);

export default RoomDrawer;
